import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpAdmin } from '../lib/http';
import { useGuard401 } from './adminShared';
import { TOWNS, serviceBoxes as SERVICE_BOXES } from './shopOwnerDashboard/utils';
import './AdminBusinessListing.css';

const DRAFT_KEY = 'adm-biz-draft-v1';
const STEPS = ['Basic', 'Hours', 'Media', 'Services', 'Staff', 'Review'];
const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOUR_SLOTS = Array.from({ length: 16 }, (_, index) => `${String(index + 8).padStart(2, '0')}:00`);
const emptyForm = () => ({
  name: '', town: '', serviceCategory: '', phone: '', mapEmbed: '',
  services: [{ serviceName: '', price: '', duration: '60' }],
});

function safeDraft() {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY));
    return saved && typeof saved === 'object' ? saved : null;
  } catch {
    return null;
  }
}

export default function AdminBusinessListing() {
  const nav = useNavigate();
  const guard = useGuard401();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [staff, setStaff] = useState([{ name: '', photo: null, preview: '', photoUrl: '' }]);
  const [days, setDays] = useState([]);
  const [slots, setSlots] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [portfolioPreviews, setPortfolioPreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [existingPortfolio, setExistingPortfolio] = useState([]);
  const [loading, setLoading] = useState(Boolean(editId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState('');

  const isEdit = Boolean(editId);
  const availability = useMemo(
    () => days.reduce((result, day) => ({ ...result, [day]: [...slots] }), {}),
    [days, slots],
  );

  useEffect(() => {
    if (isEdit) return;
    const draft = safeDraft();
    if (!draft) return;
    setForm((current) => ({ ...current, ...draft.form, services: draft.form?.services?.length ? draft.form.services : current.services }));
    setStaff(draft.staff?.length ? draft.staff.map((member) => ({ ...member, photo: null, preview: '', photoUrl: member.photoUrl || '' })) : [{ name: '', photo: null, preview: '', photoUrl: '' }]);
    setDays(Array.isArray(draft.days) ? draft.days : []);
    setSlots(Array.isArray(draft.slots) ? draft.slots : []);
    setStep(Math.min(Number(draft.step) || 0, STEPS.length - 1));
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !editId) return;
    let active = true;
    setLoading(true);
    httpAdmin.get(`/api/admin/businesses/${editId}`)
      .then((data) => {
        if (!active) return;
        const business = data.business || data;
        setForm({
          name: business.name || '',
          town: business.town || '',
          serviceCategory: business.serviceCategory === 'Tatoo and Piercing' ? 'Tattoo and Piercing' : business.serviceCategory || '',
          phone: business.phone || '',
          mapEmbed: business.mapEmbed || '',
          services: business.services?.length ? business.services.map((service) => ({
            serviceName: service.serviceName || '',
            price: String(service.price ?? ''),
            duration: String(service.duration ?? 60),
          })) : [{ serviceName: '', price: '', duration: '60' }],
        });
        const loadedAvailability = business.availability || {};
        const loadedDays = Object.keys(loadedAvailability);
        setDays(loadedDays);
        setSlots([...new Set(loadedDays.flatMap((day) => loadedAvailability[day] || []))]);
        setStaff(business.staff?.length ? business.staff.map((member) => ({
          name: member.name || '', photo: null, preview: '', photoUrl: member.photoUrl || '',
        })) : [{ name: '', photo: null, preview: '', photoUrl: '' }]);
        setExistingImages([business.imageUrl, ...(business.gallery || [])].filter(Boolean));
        setExistingPortfolio(business.portfolio || []);
      })
      .catch((requestError) => {
        if (!guard(requestError)) setError(requestError.message || 'Could not load this business.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [editId, guard, isEdit]);

  useEffect(() => {
    if (isEdit || success) return undefined;
    const timer = setTimeout(() => {
      const serializableStaff = staff.map(({ name, photoUrl }) => ({ name, photoUrl }));
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, staff: serializableStaff, days, slots, step }));
    }, 500);
    return () => clearTimeout(timer);
  }, [days, form, isEdit, slots, staff, step, success]);

  const setField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };
  const toggle = (value, setter) => setter((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  const replaceFiles = (event, setter, previewSetter, max) => {
    const files = Array.from(event.target.files || []);
    if (files.length > max) {
      setError(`You can upload up to ${max} images.`);
      return;
    }
    setError('');
    setter(files);
    previewSetter(files.map((file) => URL.createObjectURL(file)));
  };
  const updateService = (index, field, value) => setForm((current) => ({
    ...current,
    services: current.services.map((service, serviceIndex) => serviceIndex === index ? { ...service, [field]: value } : service),
  }));
  const updateStaff = (index, changes) => setStaff((current) => current.map((member, memberIndex) => memberIndex === index ? { ...member, ...changes } : member));

  const validateStep = (targetStep = step) => {
    if (targetStep === 0 && (!form.name.trim() || !form.town || !form.serviceCategory || !form.phone.trim() || !form.mapEmbed.trim())) {
      return 'Complete the business name, location, category, phone, and map link.';
    }
    if (targetStep === 1 && (!days.length || !slots.length)) return 'Select at least one available day and time slot.';
    if (targetStep === 2 && !isEdit && !imageFiles.length) return 'Upload at least one cover image.';
    if (targetStep === 3 && (!form.services.length || form.services.some((service) => !service.serviceName.trim() || !service.price || !service.duration))) {
      return 'Add at least one complete service.';
    }
    return '';
  };
  const moveTo = (targetStep) => {
    if (targetStep > step) {
      const problem = validateStep(step);
      if (problem) return setError(problem);
    }
    setError('');
    setStep(targetStep);
  };

  const submit = async () => {
    const problem = [0, 1, 2, 3].map(validateStep).find(Boolean);
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    setError('');
    const data = new FormData();
    ['name', 'town', 'serviceCategory', 'phone', 'mapEmbed'].forEach((field) => data.append(field, form[field]));
    data.append('services', JSON.stringify(form.services));
    data.append('availability', JSON.stringify(availability));
    data.append('exceptions', JSON.stringify([]));
    let photoIndex = 0;
    const completedStaff = staff.filter((member) => member.name.trim()).map((member) => {
      const details = { name: member.name.trim(), photoUrl: member.photoUrl || '' };
      if (member.photo) {
        details.photoIndex = photoIndex;
        photoIndex += 1;
      }
      return details;
    });
    data.append('staff', JSON.stringify(completedStaff));
    imageFiles.forEach((file) => data.append('imageFiles', file));
    portfolioFiles.forEach((file) => data.append('portfolioImages', file));
    staff.filter((member) => member.name.trim()).forEach((member) => {
      if (member.photo) data.append('staffPhotos', member.photo);
    });

    try {
      const response = await httpAdmin.fetch(`/api/admin/businesses${isEdit ? `/${editId}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        body: data,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const fallback = response.status === 413
          ? 'The images are too large to upload. Use JPEG or PNG files under 5MB.'
          : response.status >= 500
            ? 'The server could not save this business. Use JPEG or PNG images under 5MB and try again.'
            : 'Could not save the business.';
        const requestError = new Error(result.message || fallback);
        requestError.status = response.status;
        throw requestError;
      }
      localStorage.removeItem(DRAFT_KEY);
      setSuccess(result);
    } catch (requestError) {
      if (!guard(requestError)) setError(requestError.message || 'Could not save the business.');
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    setSuccess(null);
    setStep(0);
    setForm(emptyForm());
    setStaff([{ name: '', photo: null, preview: '', photoUrl: '' }]);
    setDays([]); setSlots([]); setImageFiles([]); setPortfolioFiles([]);
    setImagePreviews([]); setPortfolioPreviews([]); setExistingImages([]); setExistingPortfolio([]);
  };
  const copy = async (name, value) => {
    if (!value) return;
    await navigator.clipboard?.writeText(String(value)).catch(() => {});
    setCopied(name);
    setTimeout(() => setCopied(''), 1200);
  };

  if (success) {
    const credentials = success.generatedAccount;
    return <section className="adm-page"><div className="adm-biz-success">
      <span className="adm-biz-success__mark">✓</span>
      <h2>{success.message || 'Business saved successfully'}</h2>
      {credentials && <><p>Save these credentials now — the password is shown once.</p>
        <div className="adm-biz-credentials">
          {[['Shop name', credentials.shopName], ['Password', credentials.password], ['Analytics passcode', success.analyticsPasscode]].map(([label, value]) => <div className="adm-biz-credential" key={label}>
            <span>{label}</span><strong>{value || '—'}</strong>{value && <button type="button" onClick={() => copy(label, value)}>{copied === label ? 'Copied' : 'Copy'}</button>}
          </div>)}
        </div></>}
      <div className="adm-biz-actions"><button type="button" className="submit-button" onClick={() => nav('/admin/providers')}>View in Providers</button>
        {isEdit ? <button type="button" className="btn-secondary" onClick={() => nav('/admin/providers')}>Done</button> : <button type="button" className="btn-secondary" onClick={startAnother}>Create another</button>}
      </div>
    </div></section>;
  }

  if (loading) return <section className="adm-page"><div className="adm-loading">Loading business…</div></section>;

  return <section className="adm-page adm-biz-page">
    <header className="adm-page-head"><h2>{isEdit ? 'Edit business listing' : 'List new business'}</h2><p>{isEdit ? 'Update listing details without changing owner credentials.' : 'Create an approved business listing and owner account.'}</p></header>
    <form className="adm-biz-wizard" onSubmit={(event) => { event.preventDefault(); submit(); }}>
      <div className="adm-biz-steps" aria-label="Business listing progress">
        <div className="adm-biz-progress"><span style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }} /></div>
        <div className="adm-biz-step-list">{STEPS.map((name, index) => <button type="button" key={name} onClick={() => moveTo(index)} className={index === step ? 'is-active' : index < step ? 'is-complete' : ''} aria-current={index === step ? 'step' : undefined}><span>{index + 1}</span>{name}</button>)}</div>
        {error && <p className="adm-biz-error" role="alert">{error}</p>}
      </div>
      <div className="adm-biz-card">
        {step === 0 && <div className="adm-biz-fields"><h3>Basic information</h3>
          <label>Business name<input name="name" value={form.name} onChange={setField} /></label>
          <label>Town<select name="town" value={form.town} onChange={setField}><option value="">Select a town</option>{TOWNS.map((town) => <option key={town}>{town}</option>)}</select></label>
          <label>Service category<select name="serviceCategory" value={form.serviceCategory} onChange={setField}><option value="">Select a category</option>{SERVICE_BOXES.map((category) => <option key={category}>{category}</option>)}</select></label>
          <label>Phone<input name="phone" value={form.phone} onChange={setField} /></label>
          <label className="adm-biz-fields__full">Map embed URL<input name="mapEmbed" value={form.mapEmbed} onChange={setField} placeholder="https://…" /></label>
        </div>}
        {step === 1 && <div className="adm-biz-section"><h3>Hours</h3><p>Choose the days and appointment slots this business offers.</p>
          <h4>Available days</h4><div className="adm-biz-chips"><button type="button" aria-pressed={days.length === WEEK_DAYS.length} onClick={() => setDays(days.length === WEEK_DAYS.length ? [] : WEEK_DAYS)}>Select all</button>{WEEK_DAYS.map((day) => <button type="button" key={day} aria-pressed={days.includes(day)} onClick={() => toggle(day, setDays)}>{day}</button>)}</div>
          <h4>Available slots</h4><div className="adm-biz-chips"><button type="button" aria-pressed={slots.length === HOUR_SLOTS.length} onClick={() => setSlots(slots.length === HOUR_SLOTS.length ? [] : HOUR_SLOTS)}>Select all</button>{HOUR_SLOTS.map((slot) => <button type="button" key={slot} aria-pressed={slots.includes(slot)} onClick={() => toggle(slot, setSlots)}>{slot}</button>)}</div>
        </div>}
        {step === 2 && <div className="adm-biz-section"><h3>Media</h3><p>Upload up to five listing images. The first becomes the cover; new uploads replace current listing images.</p>
          <label className="adm-biz-upload">Listing images<input type="file" accept="image/*" multiple onChange={(event) => replaceFiles(event, setImageFiles, setImagePreviews, 5)} /></label>
          <PreviewGrid images={imagePreviews.length ? imagePreviews : existingImages} label="Listing" />
          <label className="adm-biz-upload">Portfolio images (optional)<input type="file" accept="image/*" multiple onChange={(event) => replaceFiles(event, setPortfolioFiles, setPortfolioPreviews, 10)} /></label>
          <PreviewGrid images={portfolioPreviews.length ? portfolioPreviews : existingPortfolio} label="Portfolio" />
        </div>}
        {step === 3 && <div className="adm-biz-section"><h3>Services</h3>{form.services.map((service, index) => <div className="adm-biz-service-row" key={index}>
          <label>Service<input value={service.serviceName} onChange={(event) => updateService(index, 'serviceName', event.target.value)} /></label>
          <label>Price<input type="number" min="0" value={service.price} onChange={(event) => updateService(index, 'price', event.target.value)} /></label>
          <label>Minutes<input type="number" min="5" max="240" value={service.duration} onChange={(event) => updateService(index, 'duration', event.target.value)} /></label>
          {form.services.length > 1 && <button type="button" className="adm-biz-remove" onClick={() => setForm((current) => ({ ...current, services: current.services.filter((_, serviceIndex) => serviceIndex !== index) }))}>Remove</button>}
        </div>)}<button type="button" className="btn-secondary" onClick={() => setForm((current) => ({ ...current, services: [...current.services, { serviceName: '', price: '', duration: '60' }] }))}>Add service</button></div>}
        {step === 4 && <div className="adm-biz-section"><h3>Staff</h3><p>Staff members are optional. Photos follow the staff order when uploaded.</p>{staff.map((member, index) => <div className="adm-biz-staff-row" key={index}>
          {(member.preview || member.photoUrl) && <img src={member.preview || member.photoUrl} alt="" className="adm-biz-staff-photo" />}
          <label>Name<input value={member.name} onChange={(event) => updateStaff(index, { name: event.target.value })} /></label>
          <label>Photo<input type="file" accept="image/*" onChange={(event) => { const file = event.target.files?.[0]; if (file) updateStaff(index, { photo: file, preview: URL.createObjectURL(file) }); }} /></label>
          {staff.length > 1 && <button type="button" className="adm-biz-remove" onClick={() => setStaff((current) => current.filter((_, staffIndex) => staffIndex !== index))}>Remove</button>}
        </div>)}<button type="button" className="btn-secondary" onClick={() => setStaff((current) => [...current, { name: '', photo: null, preview: '', photoUrl: '' }])}>Add staff member</button></div>}
        {step === 5 && <div className="adm-biz-review"><h3>Review listing</h3><ReviewRow label="Business" value={form.name} /><ReviewRow label="Location" value={`${form.town} · ${form.serviceCategory}`} /><ReviewRow label="Hours" value={`${days.length} days · ${slots.length} slots`} /><ReviewRow label="Media" value={`${imageFiles.length || existingImages.length} listing images · ${portfolioFiles.length || existingPortfolio.length} portfolio images`} /><ReviewRow label="Services" value={form.services.map((service) => service.serviceName).filter(Boolean).join(', ') || 'None'} /><ReviewRow label="Staff" value={staff.filter((member) => member.name.trim()).map((member) => member.name).join(', ') || 'None'} /></div>}
      </div>
      <footer className="adm-biz-footer">{step > 0 && <button type="button" className="btn-secondary" onClick={() => moveTo(step - 1)}>Back</button>}{step < STEPS.length - 1 ? <button type="button" className="submit-button" onClick={() => moveTo(step + 1)}>Next</button> : <button type="submit" className="submit-button" disabled={submitting}>{submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create business'}</button>}</footer>
    </form>
  </section>;
}

function PreviewGrid({ images, label }) {
  if (!images.length) return null;
  return <div className="adm-biz-preview-grid">{images.map((src, index) => <img src={src} alt={`${label} preview ${index + 1}`} key={`${src}-${index}`} />)}</div>;
}

function ReviewRow({ label, value }) {
  return <div><span>{label}</span><strong>{value || '—'}</strong></div>;
}
